// BIND zone 文件 → 记录列表(批量导入用)。
// 支持 $ORIGIN / $TTL、括号续行、注释、省略 owner(沿用上一条)、ttl/class 任意顺序。
// SOA 和 apex 的 NS 跳过(由平台管理);不支持的类型跳过并计数。

const TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA', 'PTR'];
const CLASSES = ['IN', 'CH', 'HS'];

export const looksLikeBind = (text) => !text.includes('|') && (
    /^\s*\$(ORIGIN|TTL)\b/mi.test(text)
    || /^\S*\s+(\d+[smhdw]?\s+)?(IN\s+)?(SOA|A|AAAA|CNAME|MX|TXT|NS|SRV|CAA|PTR)\s+\S/mi.test(text)
);

// 去掉注释(引号内的 ; 保留)
const stripComment = (line) => {
    let q = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '\\') { i++; continue; }
        if (c === '"') q = !q;
        else if (c === ';' && !q) return line.slice(0, i);
    }
    return line;
};

// 按空白切分,引号串作为一个 token(保留引号)
const tokenize = (s) => {
    const out = [];
    const re = /"((?:[^"\\]|\\.)*)"|(\S+)/g;
    let m;
    while ((m = re.exec(s))) out.push(m[1] !== undefined ? `"${m[1]}"` : m[2]);
    return out;
};

// 合并括号续行,返回 [{text, owner: 行首是否有 owner}]
const logicalLines = (text) => {
    const out = [];
    let buf = '';
    let depth = 0;
    let ownerPresent = false;
    for (const raw of text.split(/\r?\n/)) {
        const line = stripComment(raw);
        if (depth === 0) {
            if (!line.trim()) continue;
            ownerPresent = !/^\s/.test(line);
            buf = line;
        } else {
            buf += ' ' + line;
        }
        for (const c of line.replace(/"(?:[^"\\]|\\.)*"/g, '')) {
            if (c === '(') depth++;
            else if (c === ')') depth--;
        }
        if (depth <= 0) {
            depth = 0;
            out.push({ text: buf.replace(/[()]/g, ' '), owner: ownerPresent });
            buf = '';
        }
    }
    return out;
};

const ttlSeconds = (v) => {
    if (/^\d+$/.test(v)) return parseInt(v, 10);
    if (!/^(\d+[smhdw])+$/i.test(String(v))) return null;
    const mult = { s: 1, m: 60, h: 3600, d: 86400, w: 604800 };
    let total = 0;
    for (const [, n, u] of v.toLowerCase().matchAll(/(\d+)([smhdw])/g)) total += parseInt(n, 10) * mult[u];
    return total;
};

/**
 * @param {string} text  zone 文件内容
 * @param {string} zone  当前 zone(example.com)
 * @returns {{records: object[], skipped: number}}
 */
export const parseBind = (text, zone) => {
    const z = String(zone || '').toLowerCase().replace(/\.$/, '');
    let origin = z;
    let defTtl = 3600;
    let lastOwner = '@';
    const records = [];
    let skipped = 0;

    // 名字 → 相对 zone 的记录名(@ / www / a.b);不在本 zone 下返回 null
    const relName = (name) => {
        let n = name === '@' ? origin : (name.endsWith('.') ? name.slice(0, -1) : (origin ? `${name}.${origin}` : name));
        n = n.toLowerCase();
        if (n === z) return '@';
        if (z && n.endsWith('.' + z)) return n.slice(0, -(z.length + 1));
        return null;
    };
    // 目标名(CNAME/MX/NS 等):相对名补全 origin,去掉末尾点
    const target = (v) => (v === '@' ? origin : (v.endsWith('.') ? v.slice(0, -1) : `${v}.${origin}`));

    for (const { text: line, owner } of logicalLines(text)) {
        const tok = tokenize(line.trim());
        if (!tok.length) continue;
        const dir = tok[0].toUpperCase();
        if (dir === '$ORIGIN') { origin = (tok[1] || '').replace(/\.$/, '').toLowerCase(); continue; }
        if (dir === '$TTL') { defTtl = ttlSeconds(tok[1]) || defTtl; continue; }
        if (dir.startsWith('$')) { skipped++; continue; }

        let i = 0;
        let name = lastOwner;
        if (owner) { name = tok[0]; i = 1; }
        lastOwner = name;
        let ttl = defTtl;
        // ttl / class 任意顺序,最多各一个
        for (let k = 0; k < 2 && i < tok.length; k++) {
            const up = tok[i].toUpperCase();
            if (CLASSES.includes(up)) { i++; continue; }
            const s = ttlSeconds(tok[i]);
            if (s !== null && !TYPES.includes(up) && up !== 'SOA') { ttl = s; i++; continue; }
        }
        const type = (tok[i] || '').toUpperCase();
        const rdata = tok.slice(i + 1);
        const rel = relName(name);
        if (type === 'SOA' || (type === 'NS' && rel === '@') || !TYPES.includes(type) || rel === null || !rdata.length) {
            skipped++;
            continue;
        }
        const rec = { name: rel, type, ttl, priority: 0, content: '', comment: '' };
        if (type === 'MX') {
            rec.priority = parseInt(rdata[0], 10) || 0;
            rec.content = target(rdata[1] || '');
        } else if (type === 'SRV') {
            rec.priority = parseInt(rdata[0], 10) || 0;
            rec.content = `${rdata[1]} ${rdata[2]} ${target(rdata[3] || '')}`;
        } else if (type === 'TXT') {
            // 多个引号串按 DNS 语义拼接成一个值;平台生成 zone 时再按 255 字节切分
            rec.content = rdata.map((s) => (s.startsWith('"') ? s.slice(1, -1).replace(/\\(.)/g, '$1') : s)).join('');
        } else if (['CNAME', 'NS', 'PTR'].includes(type)) {
            rec.content = target(rdata[0]);
        } else {
            rec.content = rdata.join(' ');
        }
        records.push(rec);
    }
    return { records, skipped };
};
